import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { OwnerAppointment } from '@/api/appointments/appointmentsApi.types';
import { Icon } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  buildAppointmentStatusTranslationKey,
  buildTherapyTypeTranslationKey,
} from '@/constants/appointmentOptions';
import {
  useCancelOwnAppointmentMutation,
  useRequestOwnAppointmentRescheduleMutation,
} from '@/hooks/useOwnAppointmentActions';
import {
  formatIsoDateTimeAsWallClock,
  formatIsoDateToDayAndMonth,
} from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { CancelVisitDialog } from './CancelVisitDialog';
import { RescheduleRequestDialog } from './RescheduleRequestDialog';
import { VisitSummaryRow } from './VisitSummaryRow';

const CANCELLED_STATUS = 'cancelled';

interface VisitRowProps {
  visit: OwnerAppointment;
  priceLabel: string | null;
  onAddToCalendar: (visitId: string) => void;
}

type OpenDialog = 'none' | 'cancel' | 'reschedule';

export function VisitRow({ visit, priceLabel, onAddToCalendar }: VisitRowProps) {
  const { t } = useTranslation(['clients', 'appointments']);
  const cancelMutation = useCancelOwnAppointmentMutation();
  const rescheduleMutation = useRequestOwnAppointmentRescheduleMutation();
  const [openDialog, setOpenDialog] = useState<OpenDialog>('none');
  const hasPendingRescheduleRequest = visit.rescheduleRequestedAt !== null;

  function closeDialog() {
    setOpenDialog('none');
  }

  function cancelVisit() {
    cancelMutation.mutate(visit.id, {
      onSuccess: function done() {
        showSuccessNotification(t('clients:visits.cancelled'));
        closeDialog();
      },
      onError: function fail(error) {
        const message = resolveApiErrorMessage(error);
        showErrorNotification(message.length > 0 ? message : t('clients:visits.cancelError'));
      },
    });
  }

  function requestReschedule(message: string) {
    rescheduleMutation.mutate(
      { appointmentId: visit.id, message },
      {
        onSuccess: function done() {
          showSuccessNotification(t('clients:visits.rescheduleRequested'));
          closeDialog();
        },
        onError: function fail(error) {
          const errorMessage = resolveApiErrorMessage(error);
          showErrorNotification(
            errorMessage.length > 0 ? errorMessage : t('clients:visits.rescheduleError')
          );
        },
      }
    );
  }

  if (visit.status === CANCELLED_STATUS) {
    return <VisitSummaryRow visit={visit} />;
  }

  return (
    <div className="p-2.5 border border-zooborder rounded-xl">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-zooindigo truncate">{visit.patientName}</div>
          <div className="text-[11px] text-zoomuted truncate">
            {t(buildTherapyTypeTranslationKey(visit.therapyType))}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            <Badge tone="neutral" size="sm">
              {t(buildAppointmentStatusTranslationKey(visit.status))}
            </Badge>
            {hasPendingRescheduleRequest ? (
              <Badge tone="warning" size="sm">
                {t('clients:visits.rescheduleRequestedBadge')}
              </Badge>
            ) : null}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[13px] font-bold text-zooprimary">
            {formatIsoDateToDayAndMonth(visit.date)}
          </div>
          <div className="text-[11px] text-zoomuted">{visit.startTime}</div>
          {priceLabel !== null ? (
            <div className="text-[11px] font-medium text-zooindigo mt-0.5">{priceLabel}</div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-2">
        <Button
          variant="outline"
          size="chip"
          className="min-h-[44px]"
          onClick={function addThisVisitToCalendar() {
            onAddToCalendar(visit.id);
          }}
        >
          <Icon name="Calendar" />
          {t('clients:portal.addToCalendar')}
        </Button>
        <Button
          variant="outline"
          size="chip"
          className="min-h-[44px]"
          disabled={visit.canRequestReschedule === false}
          onClick={function openReschedule() {
            setOpenDialog('reschedule');
          }}
        >
          <Icon name="RefreshCw" />
          {t('clients:visits.requestReschedule')}
        </Button>
        <Button
          variant="destructiveGhost"
          size="chip"
          className="min-h-[44px] border border-zooborder"
          disabled={visit.canCancel === false}
          onClick={function openCancel() {
            setOpenDialog('cancel');
          }}
        >
          {t('clients:visits.cancel')}
        </Button>
      </div>
      <p className="text-[11px] text-zoomuted mt-1.5">
        {visit.canCancel
          ? t('clients:visits.cancelUntil', {
              deadline: formatIsoDateTimeAsWallClock(visit.cancellationDeadline),
            })
          : t('clients:visits.cancelClosed')}
      </p>

      {openDialog === 'cancel' ? (
        <CancelVisitDialog
          visit={visit}
          isSubmitting={cancelMutation.isPending}
          onConfirm={cancelVisit}
          onCancel={closeDialog}
        />
      ) : null}
      {openDialog === 'reschedule' ? (
        <RescheduleRequestDialog
          isSubmitting={rescheduleMutation.isPending}
          onConfirm={requestReschedule}
          onCancel={closeDialog}
        />
      ) : null}
    </div>
  );
}
